import { useSelector, useDispatch } from 'react-redux';
import { showUpgradeModal } from '../redux/slices/tierSlice';
import { isFreeOnEveryPlan } from '../../backend/planModel';
import { asPaywallContext } from '../utils/paywall';

/**
 * Hook for tier-based feature gating.
 *
 * @param {string} featureKey - A key of GOAT_FEATURES in redux/slices/tierSlice.js.
 *   Keys the plan model opens to every plan (src/backend/planModel.js — Mixpedia
 *   and For You while MIXPEDIA_FREE_FOR_ALL is on) report canAccess for
 *   everyone; main's tierCache.requireFeature makes the same call.
 * @returns {{ canAccess: boolean, tier: string, isGoat: boolean, isTrial: boolean, requireUpgrade: (context?: object) => void }}
 *   `requireUpgrade(context)` opens the paywall for this feature; `context`
 *   is an optional plain object (`{ moreCount }`, `{ total }`, `{ list }`) the
 *   wall knows and the paywall headline names. Safe to wire straight into
 *   onClick — a synthetic event is ignored, not treated as context.
 */
export function useTierGate(featureKey) {
    const dispatch = useDispatch();
    const tier = useSelector((state) => state.tier.effectiveTier);
    const reason = useSelector((state) => state.tier.reason);
    const isLoaded = useSelector((state) => state.tier.isLoaded);

    const isGoat = tier === 'goat';
    const isTrial = reason === 'trial';
    const canAccess = isGoat || isFreeOnEveryPlan(featureKey);

    const requireUpgrade = (context) => {
        if (!canAccess) {
            dispatch(showUpgradeModal({ feature: featureKey, context: asPaywallContext(context) }));
        }
    };

    return {
        canAccess,
        tier,
        isGoat,
        isTrial,
        isLoaded,
        requireUpgrade,
    };
}

export default useTierGate;
